import { useEffect } from "react";

const VERSION_POLL_INTERVAL_MS = 2 * 60 * 1000;
const RELOAD_TARGET_KEY = "suikamon:last-reloaded-build";

type VersionManifest = {
  buildId?: string;
};

export function useAppVersionRefresh() {
  useEffect(() => {
    if (import.meta.env.DEV) {
      return;
    }

    let isDisposed = false;

    const checkForNewVersion = async () => {
      if (isDisposed || document.visibilityState === "hidden") {
        return;
      }

      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
          },
        });

        if (!response.ok) {
          return;
        }

        const manifest = (await response.json()) as VersionManifest;
        const nextBuildId = manifest.buildId;

        if (!nextBuildId || nextBuildId === import.meta.env.VITE_APP_BUILD_ID) {
          return;
        }

        if (sessionStorage.getItem(RELOAD_TARGET_KEY) === nextBuildId) {
          return;
        }

        sessionStorage.setItem(RELOAD_TARGET_KEY, nextBuildId);
        window.location.reload();
      } catch {
        // Ignore transient network/cache errors and try again later.
      }
    };

    void checkForNewVersion();

    const intervalId = window.setInterval(() => {
      void checkForNewVersion();
    }, VERSION_POLL_INTERVAL_MS);

    const handleFocus = () => {
      void checkForNewVersion();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkForNewVersion();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isDisposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
}
